/**
 * STEP 6: PRODUCT SELECTION
 * 
 * Client-facing product catalog with Elegant theme
 * Three-panel layout: Fixtures | Catalog | Summary
 * 
 * Now fetches products from backend API instead of mock data
 */

import React, { useState, useMemo, useEffect } from 'react';
import '../constants/elegantTheme.css';
import {
  FixtureList,
  CatalogHeader,
  ProductGrid,
  SelectionSummary,
  ClientSuppliedModal,
  SpecialOrderModal,
  CabinetStylePicker,
} from './product-selection';
import {
  useProductCatalog,
  FIXTURE_LABELS,
  getCategoryForFixture,
} from '../hooks/useProductCatalog';

const Step6ProductSelection = ({ 
  data,
  onUpdate,
  measurementData = {},
  settings = {},
  disabled = false,
  onSave,
  onPrev,
  onNext,
  saving,
  isComplete,
}) => {
  const [activeFixture, setActiveFixture] = useState(null);
  const [activeTier, setActiveTier] = useState('all');
  const [showClientSupplied, setShowClientSupplied] = useState(false);
  const [showSpecialOrder, setShowSpecialOrder] = useState(false);

  // Use the product catalog hook to fetch products from API
  const { 
    products: allProducts, 
    loading: productsLoading, 
    error: productsError,
    getProductsByCategoryAndTier,
  } = useProductCatalog();

  // Get selections from data
  const selections = data?.selections || [];
  
  // Get areas from measurement data
  const areas = measurementData?.areas || [];

  // Get fixtures needing products from measurements
  const fixturesNeedingProducts = useMemo(() => {
    const fixtures = [];
    areas.forEach(area => {
      Object.entries(area.fixtures || {}).forEach(([key, fixture]) => {
        if (fixture.condition === 'exists_replace' || fixture.condition === 'add_new') {
          fixtures.push({
            area_id: area.area_id,
            area_label: area.label,
            area_type: area.type,
            fixture_key: key,
            fixture_label: FIXTURE_LABELS[key] || key,
            condition: fixture.condition,
            category: getCategoryForFixture(key),
            hasSelection: selections.some(
              s => s.area_id === area.area_id && s.fixture_key === key
            ),
          });
        }
      });
    });
    return fixtures;
  }, [areas, selections]);

  // Get products for active fixture
  const products = useMemo(() => {
    if (!activeFixture || productsLoading) return [];
    
    const category = getCategoryForFixture(activeFixture.fixture_key);
    return getProductsByCategoryAndTier(category, activeTier);
  }, [activeFixture, activeTier, getProductsByCategoryAndTier, productsLoading]);

  // Get markup for tier from settings
  const getMarkup = (tier) => {
    const markupSettings = settings?.markup_settings || {};
    if (markupSettings.by_tier && markupSettings.by_tier[tier]) {
      return markupSettings.by_tier[tier];
    }
    return markupSettings.default_percent || 20;
  };

  // Handle product selection
  const handleSelectProduct = (product) => {
    const markup = getMarkup(product.tier);
    const clientPrice = product.cost * (1 + markup / 100);

    const newSelection = {
      selection_id: `sel_${Date.now()}`,
      area_id: activeFixture.area_id,
      area_label: activeFixture.area_label,
      fixture_key: activeFixture.fixture_key,
      fixture_label: activeFixture.fixture_label,
      selection_type: 'catalog',
      product_id: product.product_id,
      product_name: product.name,
      product_brand: product.brand,
      product_sku: product.sku,
      product_image: product.image_url,
      product_tier: product.tier,
      catalog_cost: product.cost,
      markup_percent: markup,
      client_price: clientPrice,
      client_supplied: false,
      special_order: false,
      notes: '',
      created_at: new Date().toISOString(),
    };

    // Remove existing selection for this fixture
    const filtered = selections.filter(
      s => !(s.area_id === activeFixture.area_id && s.fixture_key === activeFixture.fixture_key)
    );

    // Update selections
    onUpdate('product_selections', {
      ...data,
      selections: [...filtered, newSelection],
    });

    // Move to next unselected fixture
    const currentIndex = fixturesNeedingProducts.findIndex(
      f => f.area_id === activeFixture.area_id && f.fixture_key === activeFixture.fixture_key
    );
    const nextFixture = fixturesNeedingProducts.find(
      (f, i) => i > currentIndex && !f.hasSelection
    );
    if (nextFixture) {
      setActiveFixture(nextFixture);
    }
  };

  // Handle remove selection
  const handleRemoveSelection = (selectionId) => {
    onUpdate('product_selections', {
      ...data,
      selections: selections.filter(s => s.selection_id !== selectionId),
    });
  };

  // Handle client supplied save
  const handleClientSuppliedSave = (clientData) => {
    const newSelection = {
      selection_id: `sel_${Date.now()}`,
      area_id: activeFixture.area_id,
      area_label: activeFixture.area_label,
      fixture_key: activeFixture.fixture_key,
      fixture_label: activeFixture.fixture_label,
      selection_type: 'client_supplied',
      product_id: null,
      product_name: clientData.product_name,
      product_brand: 'Client Supplied',
      product_sku: null,
      product_image: null,
      product_tier: null,
      catalog_cost: 0,
      markup_percent: 0,
      client_price: clientData.allowance_amount || 0,
      client_supplied: true,
      allowance_amount: clientData.allowance_amount || 0,
      special_order: false,
      notes: clientData.notes,
      created_at: new Date().toISOString(),
    };

    const filtered = selections.filter(
      s => !(s.area_id === activeFixture.area_id && s.fixture_key === activeFixture.fixture_key)
    );

    onUpdate('product_selections', {
      ...data,
      selections: [...filtered, newSelection],
    });

    setShowClientSupplied(false);
  };

  // Handle special order save
  const handleSpecialOrderSave = (orderData) => {
    const markup = getMarkup('mid');
    const clientPrice = orderData.estimated_cost * (1 + markup / 100);

    const newSelection = {
      selection_id: `sel_${Date.now()}`,
      area_id: activeFixture.area_id,
      area_label: activeFixture.area_label,
      fixture_key: activeFixture.fixture_key,
      fixture_label: activeFixture.fixture_label,
      selection_type: 'special_order',
      product_id: null,
      product_name: orderData.product_name,
      product_brand: orderData.vendor || 'Special Order',
      product_sku: null,
      product_image: null,
      product_tier: null,
      catalog_cost: orderData.estimated_cost,
      markup_percent: markup,
      client_price: clientPrice,
      client_supplied: false,
      special_order: true,
      special_order_vendor: orderData.vendor,
      special_order_lead_time: orderData.lead_time,
      special_order_estimated_cost: orderData.estimated_cost,
      notes: orderData.notes,
      created_at: new Date().toISOString(),
    };

    const filtered = selections.filter(
      s => !(s.area_id === activeFixture.area_id && s.fixture_key === activeFixture.fixture_key)
    );

    onUpdate('product_selections', {
      ...data,
      selections: [...filtered, newSelection],
    });

    setShowSpecialOrder(false);
  };

  // Handle cabinet selection
  const handleCabinetSelect = (cabinetSelection) => {
    // For cabinets, we create a selection with the style and finish info
    const newSelection = {
      selection_id: `sel_${Date.now()}`,
      area_id: activeFixture.area_id,
      area_label: activeFixture.area_label,
      fixture_key: activeFixture.fixture_key,
      fixture_label: activeFixture.fixture_label,
      selection_type: 'catalog',
      product_id: `cabinet_${cabinetSelection.style.id}_${cabinetSelection.finish.id}`,
      product_name: `${cabinetSelection.style.name} - ${cabinetSelection.finish.name}`,
      product_brand: 'Cabinet Selection',
      product_sku: null,
      product_image: cabinetSelection.style.image,
      product_tier: cabinetSelection.style.tier,
      catalog_cost: cabinetSelection.style.base_price_lf,
      markup_percent: getMarkup(cabinetSelection.style.tier),
      client_price: cabinetSelection.style.base_price_lf * (1 + getMarkup(cabinetSelection.style.tier) / 100),
      client_supplied: false,
      special_order: false,
      cabinet_style: cabinetSelection.style,
      cabinet_finish: cabinetSelection.finish,
      notes: '',
      created_at: new Date().toISOString(),
    };

    const filtered = selections.filter(
      s => !(s.area_id === activeFixture.area_id && s.fixture_key === activeFixture.fixture_key)
    );

    onUpdate('product_selections', {
      ...data,
      selections: [...filtered, newSelection],
    });
  };

  // Check if fixture uses special cabinet picker
  const useCabinetPicker = activeFixture && (
    activeFixture.fixture_key === 'base_cabinets' ||
    activeFixture.fixture_key === 'wall_cabinets' ||
    activeFixture.fixture_key === 'tall_cabinets' ||
    activeFixture.fixture_key === 'island'
  );

  // Get current selection for active fixture
  const currentSelection = activeFixture ? selections.find(
    s => s.area_id === activeFixture.area_id && s.fixture_key === activeFixture.fixture_key
  ) : null;

  return (
    <div className="elegant-theme">
      <div className="step-6-container">
        {/* Left Panel - Fixture List */}
        <FixtureList
          areas={areas}
          selections={selections}
          activeFixture={activeFixture}
          onSelectFixture={setActiveFixture}
        />

        {/* Center Panel - Product Catalog */}
        <div className="catalog-panel">
          <CatalogHeader
            fixture={activeFixture}
            activeTier={activeTier}
            onTierFilter={setActiveTier}
          />

          <div className="catalog-content">
            {productsLoading ? (
              <div className="empty-state" style={{ padding: '4rem' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                <p style={{ fontSize: '1rem', color: '#64748b' }}>Loading products...</p>
              </div>
            ) : productsError ? (
              <div className="empty-state" style={{ padding: '4rem' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
                <p style={{ fontSize: '1rem', color: '#ef4444' }}>Error loading products</p>
                <p style={{ marginTop: '0.5rem', color: '#64748b', fontSize: '0.875rem' }}>
                  {productsError}
                </p>
              </div>
            ) : !activeFixture ? (
              <div className="empty-state" style={{ padding: '4rem' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📋</div>
                <p style={{ fontSize: '1.125rem', fontWeight: 500 }}>Select a fixture from the left</p>
                <p style={{ marginTop: '0.5rem', color: '#64748b' }}>
                  to browse and select products
                </p>
              </div>
            ) : useCabinetPicker ? (
              <CabinetStylePicker
                onSelect={handleCabinetSelect}
                currentSelection={currentSelection?.cabinet_style ? {
                  style: currentSelection.cabinet_style,
                  finish: currentSelection.cabinet_finish,
                } : null}
              />
            ) : (
              <ProductGrid
                products={products}
                selectedId={currentSelection?.product_id}
                onSelect={handleSelectProduct}
                markup={getMarkup(activeTier === 'all' ? 'mid' : activeTier)}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Selection Summary */}
        <SelectionSummary
          selections={selections}
          onRemove={handleRemoveSelection}
          onClientSupplied={() => {
            if (activeFixture) setShowClientSupplied(true);
          }}
          onSpecialOrder={() => {
            if (activeFixture) setShowSpecialOrder(true);
          }}
        />
      </div>

      {/* Client Supplied Modal */}
      {showClientSupplied && (
        <ClientSuppliedModal
          fixture={activeFixture}
          onSave={handleClientSuppliedSave}
          onClose={() => setShowClientSupplied(false)}
        />
      )}

      {/* Special Order Modal */}
      {showSpecialOrder && (
        <SpecialOrderModal
          fixture={activeFixture}
          onSave={handleSpecialOrderSave}
          onClose={() => setShowSpecialOrder(false)}
        />
      )}
    </div>
  );
};

export default Step6ProductSelection;
